import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Rocket, Store, TrendingUp, CreditCard, Headphones, Wallet, ShieldCheck, ArrowRight, Loader2, CheckCircle2, Gift,
} from "lucide-react";
import { api, apiError, setToken } from "@/lib/api";
import { formatPrice } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/lib/seo";
import PhoneField, { isValidPhoneNumber } from "@/components/PhoneField";
import WhatsAppOtpDialog from "@/components/vendor/WhatsAppOtpDialog";

const ADVANTAGES = [
  "Créez votre boutique en quelques minutes",
  "Ajoutez vos produits et formations facilement",
  "Gérez vos commandes et clients",
  "Suivez vos ventes en temps réel",
  "Bénéficiez d'un accompagnement dédié",
  "Augmentez vos revenus sur une plateforme sécurisée",
];

const FEATURES = [
  { icon: Rocket, title: "Démarrage rapide", text: "Créez votre boutique et commencez à vendre en quelques minutes." },
  { icon: Store, title: "Gestion simplifiée", text: "Gérez vos produits, commandes et clients depuis un seul tableau de bord." },
  { icon: TrendingUp, title: "Augmentez vos ventes", text: "Touchez des milliers de clients déjà présents sur la plateforme ISMO-TECH." },
  { icon: CreditCard, title: "Paiements sécurisés", text: "Vos transactions sont sécurisées et les paiements gérés de manière fiable." },
  { icon: Headphones, title: "Support dédié", text: "Bénéficiez d'un accompagnement et d'une assistance dédiés à votre réussite." },
  { icon: Wallet, title: "Revenus garantis", text: "Gardez le contrôle sur vos prix et développez votre activité en toute liberté." },
];

export default function DevenirRevendeur() {
  const { user, loadUser } = useAuth();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [plans, setPlans] = useState([]);
  const [trialDays, setTrialDays] = useState(20);
  const [form, setForm] = useState({
    first_name: "", name: "", shop_name: "", email: "", phone: "", country: "Côte d'Ivoire",
    password: "", confirm: "", shop_description: "", accept: false,
  });
  const [otpOpen, setOtpOpen] = useState(false);
  const [verified, setVerified] = useState({ phone: "", token: "" });

  useEffect(() => {
    api.get("/vendor-plans").then(({ data }) => {
      setPlans(data.plans || []);
      setTrialDays(data.trial_days || 20);
    }).catch(() => {});
  }, []);

  useSeo({ title: "Devenir revendeur — ISMO-TECH", description: "Créez votre boutique et vendez vos produits et formations sur ISMO-TECH." });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    if (!form.phone) { setError("Votre numéro WhatsApp Business est obligatoire : c'est sur ce numéro que vous recevrez vos commandes."); return; }
    if (!isValidPhoneNumber(form.phone)) { setError("Numéro de téléphone invalide. Vérifiez l'indicatif du pays et le nombre de chiffres."); return; }
    if (!form.accept) { setError("Vous devez accepter les conditions."); return; }
    if (verified.token && verified.phone === form.phone) { await createAccount(verified.token); return; }
    setOtpOpen(true);
  };

  const createAccount = async (token) => {
    setLoading(true);
    try {
      const { data } = await api.post("/vendor/register", {
        first_name: form.first_name, name: form.name, shop_name: form.shop_name, email: form.email,
        phone: form.phone, country: form.country, password: form.password, shop_description: form.shop_description,
        phone_verification_token: token,
      });
      setToken(data.access_token);
      await loadUser();
      toast.success(`Compte créé avec succès ! 🎁 ${trialDays} jours d'essai gratuit activés.`);
      navigate("/vendeur/dashboard");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onVerified = async (token, phone) => {
    setOtpOpen(false);
    setVerified({ phone: form.phone, token });
    toast.success("Numéro WhatsApp vérifié ✅");
    await createAccount(token);
  };

  return (
    <div className="pb-16">
      <section className="hero-shell relative overflow-hidden">
        <div className="hero-grid-lines absolute inset-0 opacity-60" />
        <div className="container-it relative z-10 grid gap-10 py-14 lg:grid-cols-2 lg:py-20">
          <div>
            <span className="inline-block rounded-full bg-brand/15 px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] text-brand">Devenir revendeur</span>
            <h1 className="mt-5 font-display text-4xl font-black leading-tight text-white sm:text-5xl">
              Devenez revendeur sur <span className="text-brand">ISMO-TECH</span>
            </h1>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-300 sm:text-base">
              Rejoignez notre plateforme et développez votre activité en vendant vos produits, licences et formations à des milliers de clients.
            </p>
            <ul className="mt-7 space-y-2.5">
              {ADVANTAGES.map((a) => (
                <li key={a} className="flex items-center gap-2.5 text-sm text-slate-200">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-brand" /> {a}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <button data-testid="become-vendor-cta" onClick={() => { user?.role === "vendor" ? navigate("/vendeur/dashboard") : setShowForm(true); document.getElementById("vendor-form")?.scrollIntoView({ behavior: "smooth" }); }}
                className="btn-glow flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 text-sm font-bold text-white">
                {user?.role === "vendor" ? "Aller à mon tableau de bord" : "Créer mon compte revendeur"} <ArrowRight className="h-4 w-4" />
              </button>
              <Link to="/boutiques" className="flex items-center gap-2 rounded-full border border-white/25 px-6 py-3.5 text-sm font-bold text-white hover:border-brand">
                Voir les boutiques
              </Link>
            </div>
            <p className="mt-4 flex items-center gap-2 text-xs text-slate-400">
              <ShieldCheck className="h-4 w-4 text-brand" /> {trialDays} jours d'essai gratuit · Sans engagement · Annulation à tout moment
            </p>
          </div>

          <div className="flex items-start justify-center">
            <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
              <p className="font-display text-lg font-bold text-white">🎁 Offre de lancement</p>
              <p className="mt-2 text-sm text-slate-300">Chaque nouveau revendeur bénéficie automatiquement de <span className="font-bold text-brand">{trialDays} jours gratuits</span> pour tester toutes les fonctionnalités de vente.</p>
              <div className="mt-5 space-y-3">
                {["Boutique personnalisée", "Produits & formations illimités pendant l'essai", "Tableau de bord complet", "Statistiques en temps réel"].map((x) => (
                  <div key={x} className="flex items-center gap-2 rounded-xl bg-white/10 px-3 py-2.5 text-sm text-white">
                    <CheckCircle2 className="h-4 w-4 text-brand" /> {x}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-it py-14">
        <h2 className="text-center font-display text-2xl font-extrabold text-ink sm:text-3xl">Pourquoi devenir revendeur sur ISMO-TECH ?</h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, title, text }) => (
            <div key={title} className="rounded-2xl border border-line bg-white p-6 text-center transition-transform duration-200 hover:-translate-y-1">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-light text-brand"><Icon className="h-6 w-6" /></span>
              <p className="mt-4 font-display text-base font-bold text-ink">{title}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-500">{text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* -------- Offres d'abonnement + essai gratuit -------- */}
      <section className="container-it pb-6" data-testid="vendor-plans-section">
        <h2 className="text-center font-display text-2xl font-extrabold text-ink sm:text-3xl">Nos offres revendeurs</h2>
        <p className="mx-auto mt-2 max-w-xl text-center text-sm text-slate-500">Commencez gratuitement, puis choisissez l'offre adaptée à votre ambition.</p>
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {/* Essai gratuit */}
          <div className="relative flex flex-col rounded-3xl border-2 border-dashed border-emerald-300 bg-emerald-50/50 p-6" data-testid="plan-trial">
            <span className="absolute -top-3 left-5 flex items-center gap-1 rounded-full bg-emerald-500 px-3 py-1 text-[10px] font-black uppercase text-white"><Gift className="h-3 w-3" /> Gratuit</span>
            <p className="font-display text-lg font-extrabold text-ink">Essai gratuit</p>
            <p className="mt-1 font-display text-3xl font-black text-emerald-600">0 FCFA</p>
            <p className="text-xs font-bold text-slate-500">{trialDays} jours offerts</p>
            <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-600">
              {["Boutique personnalisée", "Produits & formations", "Tableau de bord complet", "Aucun engagement"].map((f) => (
                <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>
              ))}
            </ul>
            <button onClick={() => { setShowForm(true); document.getElementById("vendor-form")?.scrollIntoView({ behavior: "smooth" }); }}
              data-testid="choose-plan-trial"
              className="mt-6 w-full rounded-full bg-emerald-600 py-3 text-sm font-bold text-white hover:bg-emerald-700">
              Commencer gratuitement
            </button>
          </div>

          {plans.map((p) => (
            <div key={p.id} data-testid={`plan-card-${p.name}`}
              className={`relative flex flex-col rounded-3xl border-2 bg-white p-6 transition-transform duration-200 hover:-translate-y-1 ${p.badge ? "border-brand shadow-xl shadow-brand/10" : "border-line"}`}>
              {p.badge && <span className="absolute -top-3 left-5 rounded-full bg-brand px-3 py-1 text-[10px] font-black uppercase text-white">{p.badge}</span>}
              <p className="font-display text-lg font-extrabold text-ink">{p.name}</p>
              {p.description && <p className="mt-0.5 text-xs text-slate-500">{p.description}</p>}
              <p className="mt-1 font-display text-3xl font-black text-brand">{formatPrice(p.price)}</p>
              <p className="text-xs font-bold text-slate-500">{p.duration_days} jours{p.product_limit ? ` · ${p.product_limit} produits` : ""}{p.formation_limit ? ` · ${p.formation_limit} formations` : ""}</p>
              <ul className="mt-4 flex-1 space-y-2 text-sm text-slate-600">
                {(p.features || []).map((f) => (
                  <li key={f} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" /> {f}</li>
                ))}
              </ul>
              <button
                onClick={() => {
                  if (user?.role === "vendor") { navigate("/vendeur/abonnement"); return; }
                  setShowForm(true);
                  document.getElementById("vendor-form")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid={`choose-plan-${p.name}`}
                className={`mt-6 w-full rounded-full py-3 text-sm font-bold transition-colors ${p.badge ? "bg-brand text-white hover:bg-brand-dark" : "border-2 border-ink text-ink hover:border-brand hover:text-brand"}`}>
                Choisir {p.name}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section id="vendor-form" className="container-it">
        <div className="mx-auto max-w-2xl rounded-3xl border border-line bg-white p-6 sm:p-8">
          <h2 className="font-display text-2xl font-extrabold text-ink">Créer mon compte revendeur</h2>
          <p className="mt-1 text-sm text-slate-500">Remplissez le formulaire pour lancer votre boutique et démarrer vos 20 jours gratuits.</p>

          {user?.role === "vendor" ? (
            <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Vous êtes déjà revendeur. <Link to="/vendeur/dashboard" className="font-bold underline">Accéder à mon tableau de bord</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="vendor-register-form">
              {error && <div data-testid="vendor-register-error" className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{error}</div>}
              <div className="grid gap-4 sm:grid-cols-2">
                <Input testId="vr-firstname" label="Prénom" value={form.first_name} onChange={set("first_name")} required />
                <Input testId="vr-name" label="Nom" value={form.name} onChange={set("name")} required />
              </div>
              <Input testId="vr-shop" label="Nom de boutique" value={form.shop_name} onChange={set("shop_name")} required />
              <div className="grid gap-4 sm:grid-cols-2">
                <Input testId="vr-email" label="Email" type="email" value={form.email} onChange={set("email")} required />
                <div>
                  <PhoneField testId="vr-phone" label="Numéro WhatsApp Business *" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
                  <p className="mt-1.5 flex items-center gap-1 text-[11px] text-emerald-700" data-testid="vr-phone-hint">
                    <ShieldCheck className="h-3 w-3" /> Un code de vérification vous sera envoyé sur WhatsApp. Vos commandes arriveront sur ce numéro.
                  </p>
                </div>
              </div>
              <Input testId="vr-country" label="Pays" value={form.country} onChange={set("country")} />
              <label className="block">
                <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Description de la boutique (optionnel)</span>
                <textarea data-testid="vr-description" rows={3} value={form.shop_description} onChange={set("shop_description")}
                  className="w-full rounded-xl border border-line p-3 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10" />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <Input testId="vr-password" label="Mot de passe" type="password" value={form.password} onChange={set("password")} required minLength={6} />
                <Input testId="vr-confirm" label="Confirmation" type="password" value={form.confirm} onChange={set("confirm")} required minLength={6} />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid="vr-accept" checked={form.accept} onChange={set("accept")} className="h-4 w-4 accent-brand" />
                J'accepte les conditions de vente d'ISMO-TECH
              </label>
              <button type="submit" data-testid="vr-submit" disabled={loading}
                className="btn-glow flex w-full items-center justify-center gap-2 rounded-full bg-brand py-4 text-sm font-bold text-white disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Store className="h-4 w-4" />} {loading ? "Création du compte…" : "Vérifier mon WhatsApp et créer mon compte"}
              </button>
            </form>
          )}
        </div>
      </section>
      <WhatsAppOtpDialog open={otpOpen} phone={form.phone} email={form.email} onClose={() => setOtpOpen(false)} onVerified={onVerified} />
    </div>
  );
}

function Input({ label, testId, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <input data-testid={testId} type={type} {...rest}
        className="h-12 w-full rounded-xl border border-line px-4 text-sm focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/10" />
    </label>
  );
}
